import type { Addresses } from 'api/types/addresses.types';
import cx from 'classnames';
import InfiniteList from 'components/molecules/InfiniteList';
import React, { useCallback } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBasePath } from 'utils/router/hooks/useBasePath';

import styles from '../styles.module.scss';
import { MapsParentContainer, StyledCloseIcon, StyledComponentCloseIcon, StyledField, StyledHeader } from './AddressSearch.styles';
import AddressBox from './components/AddressBox';
import EmptyAddressesList from './components/EmptyAddressList';

const AUTO_COMPLETE_OPTIONS: google.maps.places.AutocompletionRequest = {
  types: ['establishment'],
  componentRestrictions: { country: 'ae' },
  input: '',
};

interface Props {
  goBack?: boolean;
  defaultAddress?: Addresses.Address;
}

export const AddressSearch: React.FC<Props> = ({ goBack, defaultAddress = {} }) => {
  const [isFetchingInfo, setIsFetchingInfo] = React.useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const basePath = useBasePath();

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || '',
    options: AUTO_COMPLETE_OPTIONS,
  });

  const handleCloseMap = () => {
    navigate(-1);
  };

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    getPlacePredictions({ input: event.target.value });
  };

  const onClickAddress = useCallback(
    async (place: google.maps.places.AutocompletePrediction) => {
      if (isFetchingInfo) return;
      setIsFetchingInfo(true);

      const onPlaceInfoReceived = async (placeDetails: google.maps.places.PlaceResult | null) => {
        const lat = placeDetails?.geometry?.location?.lat?.();
        const lng = placeDetails?.geometry?.location?.lng?.();
        try {
          navigate(`/${basePath}/addressGeofence?step=${1}`, {
            state: {
              lat,
              lng,
              placeId: place.place_id,
              address: defaultAddress,
              goBack,
            },
          });
          setIsFetchingInfo(false);
        } catch (error) {
          toast.error('Address is not covered');
          setIsFetchingInfo(false);
        }
      };

      placesService?.getDetails(
        {
          placeId: place.place_id,
        },
        onPlaceInfoReceived
      );
    },
    [basePath, goBack, navigate, placesService, isFetchingInfo]
  );

  return (
    <MapsParentContainer>
      <Row>
        <Col className="p-0" style={{ height: '150px' }}>
          <StyledHeader className={cx('fixed-top d-flex')}>
            <StyledComponentCloseIcon onClick={handleCloseMap}>
              <StyledCloseIcon />
            </StyledComponentCloseIcon>
            <Container>
              <StyledField
                disabled={isFetchingInfo}
                type="text"
                placeholder={t('common.searchMap')}
                className={styles.searchBox}
                onFocus={onFocus}
                onChange={onChange}
                autoFocus
              />
            </Container>
          </StyledHeader>
        </Col>
      </Row>

      <Row>
        <Col>
          <InfiniteList ListEmptyComponent={EmptyAddressesList} isInitialLoading={isPlacePredictionsLoading} data={placePredictions}>
            {(place) => <AddressBox onClick={onClickAddress} key={place.place_id} place={place} />}
          </InfiniteList>
        </Col>
      </Row>
    </MapsParentContainer>
  );
};
